import { IPSOS_AUTOMOTRIZ } from "./automotriz";
import { IPSOS_BANCOS } from "./bancos";
import { IPSOS_ALCOHOLICAS, IPSOS_NO_ALCOHOLICAS } from "./bebidas";
import { IPSOS_BELLEZA } from "./belleza";
import { IPSOS_DELIVERY } from "./delivery";
import { IPSOS_FASTFOOD } from "./fastfood";
import { IPSOS_INVERSIONES } from "./inversiones";
import { IPSOS_MASCOTAS } from "./mascotas";
import { IPSOS_PERFUMES } from "./perfumes";
import { IPSOS_SUPER, IPSOS_CONVENIENCIA } from "./retail";
import { IPSOS_SMARTPHONES } from "./smartphones";
import { IPSOS_MOVIL, IPSOS_HOGAR } from "./telcos";
import { IPSOS_TRANSPORTE } from "./transporte";
import { IPSOS_VACACIONES } from "./vacaciones";
import { IPSOS_VESTIMENTA } from "./vestimenta";
import { IPSOS_LABORALES } from "./viajes";

// Fase 5 de /categorias — frase de rubro para los correos del Radar Comercial
// (docs/modulos/categorias-analisis-mercado.md, docs/decisiones/2026-10-07-categorias-fase5-frase-correos.md).
//
// Mapeo APROBADO POR IGNACIO (categoria_id → rubro, 10.7 — por ID, nunca por nombre). Una frase de
// rubro combina TODOS los datos Ipsos relevantes del rubro, no elige entre una página de
// /categorias u otra — por eso varias claves de `FRASES` leen de más de un archivo (ej. "viajes"
// lee `vacaciones.js` + `viajes.js`). Las categorías sin página (deporte, entretenimiento, salud,
// seguridad, etc.) quedan fuera de esta fase — ningún correo las cita todavía.
export const CATEGORIA_ID_A_FRASE = {
  4: "automotriz",
  5: "automotriz",
  6: "automotriz",
  45: "automotriz",
  51: "automotriz",
  9: "bebidas",
  10: "bebidas",
  54: "bebidas",
  11: "bellezaPerfumes",
  12: "delivery",
  7: "fastfood",
  17: "fastfood",
  24: "mascotas",
  26: "retail",
  33: "retail",
  35: "smartphones",
  52: "smartphones",
  37: "telcos",
  23: "transporte",
  39: "vestimenta",
  2: "viajes",
  40: "viajes",
  19: "hoteleria",
  8: "banca",
  31: "banca",
  34: "banca",
};

const personasFmt = (n) => Math.round(n).toLocaleString("es-CL");
const pct = (valorDecimal) => `${Math.round(valorDecimal * 100)} %`;

// Chile cita el estudio sin más; Perú SIEMPRE aclara que se hizo en la red de Chile (excepción
// aprobada por Ignacio a 10.281, mismo criterio que `CategoriaLeyendaIpsos`/`plantillasRadar.js`).
// La cláusula se inserta una sola vez por frase, en la primera oración, SIEMPRE cerrada con ":"
// antes de la cantidad de personas — nunca ", son X" (corrección de redacción, 2026-10-07: el
// empalme de comas leía mal).
const fuenteIpsos = (pais) =>
  pais === "Perú"
    ? "según el estudio Ipsos 2025 realizado en nuestra red de Chile"
    : "según nuestro estudio Ipsos 2025";

const FRASES = {
  automotriz: (pais, personas) => {
    const tieneAuto = IPSOS_AUTOMOTRIZ.tieneAuto.valor;
    const cambia39 =
      IPSOS_AUTOMOTRIZ.frecuencias.gamaMedia1.share.valor + IPSOS_AUTOMOTRIZ.frecuencias.gamaMedia2.share.valor;
    return (
      `El ${pct(tieneAuto)} de quienes trabajan en los edificios de nuestra red tiene auto, ${fuenteIpsos(pais)}: ` +
      `son ${personasFmt(personas * tieneAuto)} conductores que pasan frente a tu marca todos los días. ` +
      `El ${pct(cambia39)} de ellos cambia de vehículo entre los 3 y los 9 años.`
    );
  },

  // Lám. 56 (consume/cerveza, base 275) y lám. 58 (light o zero, base 400 — pregunta general,
  // no condicionada a "quienes consumen no alcohólicas", corregido 2026-10-07).
  bebidas: (pais, personas) => {
    const consumeAlc = IPSOS_ALCOHOLICAS.consume.valor;
    const cerveza = IPSOS_ALCOHOLICAS.tipoPreferido.Cerveza.valor;
    const lightZero = IPSOS_NO_ALCOHOLICAS.consumeLightZero.valor;
    return (
      `El ${pct(consumeAlc)} de quienes trabajan en los edificios de nuestra red consume bebidas alcohólicas, ${fuenteIpsos(pais)}: ` +
      `son ${personasFmt(personas * consumeAlc)} personas frente a tu marca cada día. La mayoría prefiere cerveza (${pct(cerveza)}). ` +
      `El ${pct(lightZero)} consume bebidas light o zero.`
    );
  },

  // Lám. 60 (belleza, "usa con regularidad") y lám. 108 (perfumes, "de forma habitual" — no "a
  // diario", corregido 2026-10-07).
  bellezaPerfumes: (pais, personas) => {
    const usaBelleza = IPSOS_BELLEZA.penetracion.usa.valor;
    const usaPerfume = IPSOS_PERFUMES.penetracion.valor;
    return (
      `El ${pct(usaBelleza)} de quienes trabajan en los edificios de nuestra red usa productos de cuidado personal ` +
      `con regularidad, ${fuenteIpsos(pais)}: son ${personasFmt(personas * usaBelleza)} compradores potenciales. ` +
      `El ${pct(usaPerfume)} usa perfume habitualmente.`
    );
  },

  // Lám. 68: Uber Eats es la app que usan con MAYOR FRECUENCIA, no la "preferida" (corregido 2026-10-07).
  delivery: (pais, personas) => {
    const usaDelivery = IPSOS_DELIVERY.usaDelivery.valor;
    const uberEats = IPSOS_DELIVERY.apps["Uber Eats"].valor;
    return (
      `El ${pct(usaDelivery)} de quienes trabajan en los edificios de nuestra red usa aplicaciones de delivery de comida, ` +
      `${fuenteIpsos(pais)}: son ${personasFmt(personas * usaDelivery)} usuarios listos para pedir. ` +
      `Uber Eats es la más usada (${pct(uberEats)}).`
    );
  },

  // Lám. 75: "al menos una vez por semana" = unaVezSemana + dosOMasVecesSemana (49,2+19,0=68,2%),
  // no solo unaVezSemana (corregido 2026-10-07, la frase anterior mezclaba ambos números).
  fastfood: (pais, personas) => {
    const consume = IPSOS_FASTFOOD.consumeFastFood.valor;
    const semanalOMas =
      IPSOS_FASTFOOD.frecuenciaConsumo.unaVezSemana.pct.valor +
      IPSOS_FASTFOOD.frecuenciaConsumo.dosOMasVecesSemana.pct.valor;
    return (
      `El ${pct(consume)} de quienes trabajan en los edificios de nuestra red consume comida rápida, ${fuenteIpsos(pais)}: ` +
      `son ${personasFmt(personas * consume)} potenciales clientes cada día. ` +
      `7 de cada 10 lo hacen al menos una vez por semana (${pct(semanalOMas)}).`
    );
  },

  mascotas: (pais, personas) => {
    const tiene = IPSOS_MASCOTAS.penetracion.valor;
    const importante = IPSOS_MASCOTAS.importancia.muyImportante.valor;
    return (
      `El ${pct(tiene)} de quienes trabajan en los edificios de nuestra red tiene mascota, ${fuenteIpsos(pais)}: ` +
      `son ${personasFmt(personas * tiene)} dueños que compran alimento todos los meses. ` +
      `El ${pct(importante)} la considera muy importante en su familia.`
    );
  },

  retail: (pais, personas) => {
    const super_ = IPSOS_SUPER.penetracion.valor;
    const conveniencia = IPSOS_CONVENIENCIA.penetracion.valor;
    return (
      `El ${pct(super_)} de quienes trabajan en los edificios de nuestra red hace las compras del hogar en el ` +
      `supermercado, ${fuenteIpsos(pais)}: son ${personasFmt(personas * super_)} decisores de compra a pasos de tu local. ` +
      `El ${pct(conveniencia)} además pasa por una tienda de conveniencia.`
    );
  },

  // "son X equipos" sumaba corporativos sin contarlos en el cálculo (P × personal); pasa a "X
  // personas con smartphone" (corregido 2026-10-07, lám. 87/96).
  smartphones: (pais, personas) => {
    const tiene = IPSOS_SMARTPHONES.tieneSmartphone.valor;
    const corporativo = IPSOS_SMARTPHONES.tieneCorporativo.valor;
    const renueva2 =
      IPSOS_SMARTPHONES.frecuenciaRenovacion.cadaAno.valor + IPSOS_SMARTPHONES.frecuenciaRenovacion.cada2Anos.valor;
    return (
      `El ${pct(tiene)} de quienes trabajan en los edificios de nuestra red tiene smartphone personal, ${fuenteIpsos(pais)}: ` +
      `son ${personasFmt(personas * tiene)} personas con smartphone. El ${pct(corporativo)} además tiene uno corporativo, ` +
      `y el ${pct(renueva2)} renueva su equipo cada dos años o menos.`
    );
  },

  telcos: (pais, personas) => {
    const cambiaria = IPSOS_MOVIL.cambiariaPlan.valor;
    const corporativo = IPSOS_MOVIL.telefonoCorporativo.valor;
    const internetFijo = IPSOS_HOGAR.tieneInternetFijo.valor;
    return (
      `El ${pct(cambiaria)} de quienes trabajan en los edificios de nuestra red cambiaría de operador móvil por ` +
      `una oferta tentadora, ${fuenteIpsos(pais)}: son ${personasFmt(personas * cambiaria)} líneas en juego frente a ` +
      `tu pantalla. El ${pct(corporativo)} tiene línea corporativa, y el ${pct(internetFijo)} tiene internet fijo en su casa.`
    );
  },

  transporte: (pais, personas) => {
    const usaApps = IPSOS_TRANSPORTE.usaApps.valor;
    const rapidez = IPSOS_TRANSPORTE.valoracion.Rapidez.valor;
    return (
      `El ${pct(usaApps)} de quienes trabajan en los edificios de nuestra red usa aplicaciones de transporte, ${fuenteIpsos(pais)}: ` +
      `son ${personasFmt(personas * usaApps)} pasajeros que deciden su próximo viaje saliendo del ascensor. ` +
      `La rapidez es lo que más valoran (${pct(rapidez)}).`
    );
  },

  // "son X personas que compran ropa para el trabajo cada mes" (la cifra siempre fue solo ropa de
  // trabajo, P × laboral.mensual — el sustantivo quedaba ambiguo, corregido 2026-10-07, lám. 102).
  vestimenta: (pais, personas) => {
    const laboral = IPSOS_VESTIMENTA.laboral.frecuenciaCompra.mensual.pct.valor;
    const personal = IPSOS_VESTIMENTA.personal.frecuenciaCompra.mensual.pct.valor;
    return (
      `El ${pct(laboral)} de quienes trabajan en los edificios de nuestra red compra ropa de trabajo todos los meses, ` +
      `${fuenteIpsos(pais)}: son ${personasFmt(personas * laboral)} personas que compran ropa para el trabajo cada mes. ` +
      `El ${pct(personal)} compra ropa personal con la misma frecuencia.`
    );
  },

  viajes: (pais, personas) => {
    const saleVacaciones =
      IPSOS_VACACIONES.frecuenciaVacaciones.unaVez.pct.valor +
      IPSOS_VACACIONES.frecuenciaVacaciones.dosVeces.pct.valor +
      IPSOS_VACACIONES.frecuenciaVacaciones.variasVeces.pct.valor;
    const latam = IPSOS_VACACIONES.aerolineas.Latam.valor;
    const internacional = IPSOS_VACACIONES.tipoDestino.Internacional.valor;
    const viajaTrabajo = IPSOS_LABORALES.viajan.valor;
    return (
      `El ${pct(saleVacaciones)} de quienes trabajan en los edificios de nuestra red sale de vacaciones al menos ` +
      `una vez al año, ${fuenteIpsos(pais)}: son ${personasFmt(personas * saleVacaciones)} viajeros planificando su próximo ` +
      `destino. Latam es la aerolínea preferida (${pct(latam)}) y el ${pct(internacional)} prefiere destinos internacionales. ` +
      `El ${pct(viajaTrabajo)} además viaja por trabajo.`
    );
  },

  hoteleria: (pais, personas) => {
    const viajaTrabajo = IPSOS_LABORALES.viajan.valor;
    const hotel = IPSOS_LABORALES.hospedaje.Hotel.valor;
    const saleVacaciones =
      IPSOS_VACACIONES.frecuenciaVacaciones.unaVez.pct.valor +
      IPSOS_VACACIONES.frecuenciaVacaciones.dosVeces.pct.valor +
      IPSOS_VACACIONES.frecuenciaVacaciones.variasVeces.pct.valor;
    return (
      `El ${pct(viajaTrabajo)} de quienes trabajan en los edificios de nuestra red viaja por motivos de trabajo, ${fuenteIpsos(pais)}: ` +
      `son ${personasFmt(personas * viajaTrabajo * hotel)} huéspedes corporativos en potencia. El ${pct(hotel)} de ellos se hospeda ` +
      `en hotel, y el ${pct(saleVacaciones)} sale de vacaciones al menos una vez al año.`
    );
  },

  // "son X personas bancarizadas" (no "cuentas activas", dato que Ipsos no mide — corregido
  // 2026-10-07, lám. 50).
  banca: (pais, personas) => {
    const bancarizado = IPSOS_BANCOS.bancarizado.valor;
    const tarjeta = IPSOS_BANCOS.tieneTarjetaCredito.valor;
    const digital = IPSOS_BANCOS.usaBancaDigital.valor;
    const invierte = IPSOS_INVERSIONES.realizaInversiones.valor;
    return (
      `El ${pct(bancarizado)} de quienes trabajan en los edificios de nuestra red es cliente de un banco, ${fuenteIpsos(pais)}: ` +
      `son ${personasFmt(personas * bancarizado)} personas bancarizadas. El ${pct(tarjeta)} tiene tarjeta de crédito, ` +
      `el ${pct(digital)} usa banca digital o billeteras virtuales, y el ${pct(invierte)} además invierte sus ahorros.`
    );
  },
};

/**
 * Frase de rubro para el correo del Radar — todo o nada: "" si el rubro del cliente no tiene
 * mapeo, si no hay personas del universo, o si el país no es Chile ni Perú (10.281). Nunca un
 * número suelto o una frase a medias.
 */
export const fraseCategoria = ({ categoriaId, pais, personas }) => {
  if (pais !== "Chile" && pais !== "Perú") return "";
  if (!personas) return "";
  const rubro = CATEGORIA_ID_A_FRASE[categoriaId];
  if (!rubro) return "";
  return FRASES[rubro](pais, personas);
};
